<template>
  <LoginLayout>
    <RoutineLogoIcon :is-colored="true" class="logo" />
    <h1>Your privacy is paramount</h1>
    <h2>Routine has been designed with security and privacy in mind.</h2>
    <section class="promises">
      <div class="block">
        <header><LoginVerifiedIcon />Verified</header>
        <p>
          Every Routine app has been thoroughly verified by Google, Microsoft
          and Apple.
        </p>
      </div>
      <div class="block">
        <header><LoginDataIcon />Data</header>
        <p>
          Your data is stored on your devices and available at all times, even
          when you are offline.
        </p>
      </div>
      <div class="block">
        <header><LoginEncryptedIcon />Encrypted</header>
        <p>
          All communications are encrypted to ensure the safety of your data.
        </p>
      </div>
      <div class="block">
        <header><LoginRespectIcon />Respect</header>
        <p>
          We will never ever share nor sell your data to anyone. Under any
          circumstance!
        </p>
      </div>
    </section>
    <p class="learn">
      Learn more about the
      <BaseLink
        size="small"
        @click="
          openUrl(
            'https://help.routine.co/articles/8046819-privacy-and-security'
          )
        "
      >
        permissions
      </BaseLink>
      Routine requires, why and how they are used.
    </p>
    <BaseButton
      kind="important"
      type="secondary"
      text="Continue →"
      @click="$emit('nextStep')"
    />
  </LoginLayout>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import LoginDataIcon from "@/components/icons/login/DataIcon.vue";
import LoginEncryptedIcon from "@/components/icons/login/EncryptedIcon.vue";
import LoginRespectIcon from "@/components/icons/login/RespectIcon.vue";
import LoginVerifiedIcon from "@/components/icons/login/VerifiedIcon.vue";
import LoginLayout from "@/components/login/LoginLayout.vue";
import { metrics } from "@/metrics";
import { openExternalUrl } from "@/utils/open-external-url";

import BaseButton from "../base/BaseButton.vue";
import BaseLink from "../base/BaseLink.vue";
import RoutineLogoIcon from "../icons/RoutineLogoIcon.vue";

export default defineComponent({
  components: {
    BaseButton,
    BaseLink,
    LoginDataIcon,
    LoginEncryptedIcon,
    LoginLayout,
    LoginRespectIcon,
    LoginVerifiedIcon,
    RoutineLogoIcon,
  },

  emits: {
    nextStep: () => true,
  },

  setup() {
    const openUrl = (url: string) => openExternalUrl(url, null, false);

    metrics.segment.page("Onboarding", { step: "privacy" });

    return {
      openUrl,
    };
  },
});
</script>

<style lang="scss" scoped>
.logo {
  margin-top: 32px;
}

p {
  @include themed() {
    color: t($grey-50);
  }
}

section.promises {
  display: flex;
  flex-direction: row;
  column-gap: 16px;
  margin: 24px 0 16px 0;
  max-width: 1000px;

  @include media("<=medium") {
    flex-wrap: wrap;
    padding: 0 16px;
  }

  .block {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 10px;
    padding: 24px;
    row-gap: 8px;
    @include themed() {
      border: 0.5px solid t($grey-30);
    }

    @include media("<=medium") {
      padding: 16px;
      margin: 4px 0;
    }

    header {
      display: flex;
      flex-direction: row;
      align-items: center;
      column-gap: 8px;
      font-weight: 600;
    }
    p {
      @extend %font-caption-regular;
      text-align: start;

      @include media("<=medium") {
        margin: 4px 0 0 0;
      }
    }
  }
}

p.learn {
  margin-bottom: 24px;
  padding: 0 24px;
  @extend %font-small-regular;
}
</style>
